/**
 * Derives the canonical ALV domain order from the sidebar navigation
 * (src/components/navigationItems.js) and provides helpers to sort
 * arbitrary items by their domain identifier accordingly.
 *
 * Single source of truth = the sidebar navigation. Each nav item carries
 * explicit aliases (`mappingsDomain`, `storyblokDomain`) so we can read
 * them directly without any string conversion or override map.
 *
 * Ordering rules used by both helpers:
 *   - empty/null domain   → top    (index = -1, sorts before everything)
 *   - unknown non-empty   → bottom (index = MAX_SAFE_INTEGER)
 *   - known               → position derived from sidebar nav order
 */

import { buildNavigationItems } from '@/components/navigationItems'

function flattenNavLeaves() {
    const stub$t = (key) => key
    const items = buildNavigationItems(stub$t)
    const leaves = []
    for (const section of items) {
        if (section.to) {
            leaves.push(section)
            continue
        }
        if (Array.isArray(section.items)) {
            for (const child of section.items) leaves.push(child)
        }
    }
    return leaves
}

function getOrderedAliases(aliasField) {
    return flattenNavLeaves()
        .map((leaf) => leaf[aliasField])
        .filter(Boolean)
}

function sortByAlias(items, order, getItemDomain) {
    const indexOf = (item) => {
        const domain = getItemDomain(item)
        if (!domain) return -1
        const idx = order.indexOf(domain)
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
    }
    return [...items].sort((a, b) => indexOf(a) - indexOf(b))
}

/**
 * Sort items by their `mappings_domain` field (DB format, underscores).
 *   { mappings_domain: 'label_sales_data', ... }
 */
export function sortByMappingsDomain(items) {
    const order = getOrderedAliases('mappingsDomain')
    return sortByAlias(items, order, (item) => item && item.mappings_domain)
}

/**
 * Sort items by their `storyblokDomain` field (Storyblok format, hyphens).
 *   { storyblokDomain: 'label-sales-data', ... }
 */
export function sortByStoryblokDomain(items) {
    const order = getOrderedAliases('storyblokDomain')
    return sortByAlias(items, order, (item) => item && item.storyblokDomain)
}
